import type { TFunction } from 'i18next';

import type { ActiveFilterChip } from '@/features/header-filters';
import { formatDisplayDate } from '@/shared/lib';

import type { ReceiveLossesUrlState } from './types';

interface BuildReceiveLossesFilterChipsArgs {
  state: ReceiveLossesUrlState;
  t: TFunction;
  onRemove: (key: string) => void;
}

/**
 * Derives the removable active-filter chips from the committed losses filter
 * state (search excluded — it lives in the header search box). The date range
 * is one chip; removing it clears both bounds (`date` key, mirroring the
 * consignments / receive-history pages). Pure so it can be unit-tested
 * independently of the hook.
 */
export function buildReceiveLossesFilterChips({
  state,
  onRemove
}: BuildReceiveLossesFilterChipsArgs): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (state.consignmentNumber) {
    chips.push({
      key: 'consignmentNumber',
      label: state.consignmentNumber,
      onRemove: () => onRemove('consignmentNumber')
    });
  }

  if (state.dateFrom || state.dateTo) {
    const label = [
      state.dateFrom ? formatDisplayDate(state.dateFrom) : null,
      state.dateTo ? formatDisplayDate(state.dateTo) : null
    ]
      .filter(Boolean)
      .join(' – ');

    chips.push({ key: 'date', label, onRemove: () => onRemove('date') });
  }

  return chips;
}
